import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Loader2, Phone, Search as SearchIcon, Flame, ArrowRight, Filter, RefreshCw, TrendingUp } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import AIScoreBadge, { tierConfig } from "../components/entity-detail/AIScoreBadge";
import SentimentTierCorrelationCard from "../components/coaching/SentimentTierCorrelationCard";

const eur = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const TIERS = [
  { id: "hot", label: "🔥 Chaud" },
  { id: "warm", label: "🌤️ Tiède" },
  { id: "cold", label: "❄️ Froid" },
];

export default function HotLeadsPage() {
  const { axiosAuth } = useAuth();
  const { societeApi } = useSociete?.() || {};
  const api = useMemo(() => (societeApi ? societeApi() : axiosAuth()), [axiosAuth, societeApi]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criteria, setCriteria] = useState({
    tiers: ["hot", "warm"],
    min_score: 50,
    kind: "all",
    q: "",
    module: "",
    source: "",
  });
  const [calling, setCalling] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "50",
        min_score: String(criteria.min_score),
        tiers: criteria.tiers.join(","),
        kind: criteria.kind,
        q: criteria.q,
        module: criteria.module,
        source: criteria.source,
      });
      const r = await api.get(`/crm/leads/hot-list?${params.toString()}`);
      setItems(r.data?.items || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.tiers.join(","), criteria.min_score, criteria.kind, criteria.q, criteria.module, criteria.source]);

  useEffect(() => {
    reload();
  }, [reload]);

  const callNow = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.phone) {
      alert("Pas de numéro de téléphone enregistré.");
      return;
    }
    setCalling(item.entity_id);
    try {
      const body = item.kind === "prospect"
        ? { prospect_id: item.entity_id, phone: item.phone }
        : { phone: item.phone };
      await api.post("/aircall/click-to-call", body);
    } catch (err) {
      alert(err.response?.data?.detail || "Aircall indisponible.");
    } finally {
      setCalling(null);
    }
  };

  const toggleTier = (t) =>
    setCriteria((c) => ({ ...c, tiers: c.tiers.includes(t) ? c.tiers.filter((x) => x !== t) : [...c.tiers, t] }));

  // Aggregate stats
  const stats = useMemo(() => {
    const total = items.length;
    const byTier = items.reduce((acc, it) => ({ ...acc, [it.tier]: (acc[it.tier] || 0) + 1 }), {});
    const avgScore = total ? Math.round(items.reduce((s, it) => s + (it.score || 0), 0) / total) : 0;
    const totalAmount = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    return { total, byTier, avgScore, totalAmount };
  }, [items]);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="hot-leads-page">
      <PageHeader
        icon={<Flame />}
        title="Leads chauds"
        description="Vue centralisée de vos prospects et opportunités scorés par l'IA — filtrez, appelez, agissez."
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-5">
        <KpiCard label="Total leads" value={stats.total} accent="#f7c935" />
        <KpiCard label="🔥 Chauds" value={stats.byTier.hot || 0} accent="#f43f5e" />
        <KpiCard label="🌤️ Tièdes" value={stats.byTier.warm || 0} accent="#f59e0b" />
        <KpiCard label="Score moyen" value={`${stats.avgScore}/100`} accent="#3b82f6" />
        <KpiCard label="Pipeline" value={eur(stats.totalAmount)} accent="#22c55e" />
      </div>

      {/* Coaching IA — corrélation sentiment ↔ tier */}
      <div className="mb-5">
        <SentimentTierCorrelationCard />
      </div>

      {/* Filtres */}
      <div className="bg-[#14101c] border border-white/5 rounded-2xl p-4 mb-4" data-testid="hot-leads-filters">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
            <Filter className="w-4 h-4 text-[#f7c935]" /> Filtres
          </h3>
          <Button onClick={reload} size="sm" variant="outline" className="border-white/10 text-slate-300 text-xs" data-testid="hot-leads-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        </div>
        <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Recherche</label>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                placeholder="Nom, entreprise, email…"
                value={criteria.q}
                onChange={(e) => setCriteria((c) => ({ ...c, q: e.target.value }))}
                className="pl-9 bg-[#0b0811] border-white/10 text-white text-sm"
                data-testid="hot-leads-search"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Type</label>
            <select
              value={criteria.kind}
              onChange={(e) => setCriteria((c) => ({ ...c, kind: e.target.value }))}
              className="bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="hot-leads-kind"
            >
              <option value="all">Tous</option>
              <option value="prospect">Prospects</option>
              <option value="opportunity">Opportunités</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Score min.</label>
            <Input
              type="number"
              min={0}
              max={100}
              value={criteria.min_score}
              onChange={(e) => setCriteria((c) => ({ ...c, min_score: parseInt(e.target.value || "0", 10) }))}
              className="bg-[#0b0811] border-white/10 text-white text-sm w-20"
              data-testid="hot-leads-min-score"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Tiers IA</label>
            <div className="flex gap-1">
              {TIERS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => toggleTier(t.id)}
                  className={`text-xs px-2 py-1.5 rounded-md transition-all ${criteria.tiers.includes(t.id) ? "bg-[#f7c935] text-[#14101c] font-bold" : "bg-white/5 text-slate-400"}`}
                  data-testid={`hot-leads-tier-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-[#14101c] border border-white/5 rounded-2xl overflow-hidden">
        {loading && !items.length ? (
          <div className="p-8 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Chargement…
          </div>
        ) : !items.length ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="hot-leads-empty">
            <Flame className="w-12 h-12 mx-auto mb-3 text-slate-700" />
            Aucun lead ne correspond à ces critères.
            <br />Ajustez les filtres ou lancez plus d'évaluations IA depuis vos fiches.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="hot-leads-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left px-4 py-3">Lead</th>
                <th className="text-left px-3 py-3 hidden md:table-cell">Score IA</th>
                <th className="text-left px-3 py-3 hidden lg:table-cell">Verdict</th>
                <th className="text-left px-3 py-3 hidden lg:table-cell">Action</th>
                <th className="text-left px-3 py-3 hidden md:table-cell">Statut</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const cfg = tierConfig(it.tier);
                const initials = (it.name || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
                const action = it.next_actions?.[0];
                return (
                  <tr
                    key={`${it.kind}_${it.entity_id}`}
                    className="border-t border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`hot-lead-row-${it.entity_id}`}
                  >
                    <td className="px-4 py-3">
                      <Link to={it.url} className="flex items-center gap-2.5 group">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                          {initials || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-white truncate group-hover:text-[#f7c935]">{it.name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{it.company || it.email || "—"}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <AIScoreBadge tier={it.tier} score={it.score} size="sm" />
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell max-w-md">
                      <div className="text-xs text-slate-300 line-clamp-2 italic">« {it.headline || "—"} »</div>
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell max-w-md">
                      {action ? (
                        <div className="text-xs text-amber-300 flex items-start gap-1.5 line-clamp-2">
                          <span className="text-[#f7c935] flex-shrink-0">→</span>
                          {action}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 font-medium">
                        {it.status || "—"}
                      </span>
                      {it.kind === "opportunity" && it.amount > 0 && (
                        <div className="text-[10px] text-emerald-400 mt-1">{eur(it.amount)} ({it.probability}%)</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {it.phone && (
                          <button
                            onClick={(e) => callNow(e, it)}
                            disabled={calling === it.entity_id}
                            className="w-8 h-8 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 flex items-center justify-center disabled:opacity-50"
                            title={`Appeler ${it.phone}`}
                            data-testid={`call-${it.entity_id}`}
                          >
                            {calling === it.entity_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                          </button>
                        )}
                        <Link
                          to={it.url}
                          className="w-8 h-8 rounded-lg bg-[#f7c935]/15 hover:bg-[#f7c935]/30 text-[#f7c935] flex items-center justify-center"
                          data-testid={`open-${it.entity_id}`}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        <TrendingUp className="w-3 h-3" />
        Conseil : appelez en priorité les leads CHAUDS — le ROI moyen est x3 plus élevé que sur les TIÈDES.
      </div>
    </div>
  );
}

const KpiCard = ({ label, value, accent }) => (
  <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5" style={{ borderTopColor: accent, borderTopWidth: 2 }}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="text-xl font-bold text-white mt-1">{value}</div>
  </div>
);
